import * as JsSearch from "js-search";
import { setWithExpiry, getWithExpiry } from "../shared/utils";

// Private variables
let allUsers, jsSearch, storageKey, useRemoteSearch, initPromise, resolveInit;

function init(accountId, userCount) {
  initPromise = new Promise((resolve) => {
    resolveInit = resolve;
  });

  // Use remote search if there are >2500 users
  useRemoteSearch = userCount > 2500;

  if (useRemoteSearch) {
    resolveInit();
    return;
  }

  // Dynamically create localStorage key
  storageKey = `userSearch_${accountId}_v5`;

  jsSearch = buildJsSearch();

  // Attempt to load users from cache
  allUsers = getWithExpiry(storageKey);

  // If cached users exist, build search index
  if (allUsers) {
    jsSearch.addDocuments(allUsers);
    resolveInit();
  }

  fetchUsers();
}

function buildJsSearch() {
  const searchObj = new JsSearch.Search("id");
  searchObj.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
  searchObj.addIndex("normalized_name");
  searchObj.addIndex("name");
  searchObj.addIndex("email");

  return searchObj;
}

function rebuildSearch(users) {
  // Rebuild search index if it is not set or expired
  if (
    getWithExpiry(storageKey) === null ||
    !getWithExpiry(storageKey)?.length
  ) {
    const newJsSearch = buildJsSearch();
    newJsSearch.addDocuments(users);

    jsSearch = newJsSearch;

    allUsers = users;
  }

  // Cache users so we can instantly build indexes on page reload
  setWithExpiry(storageKey, users, 1000 * 60 * 60 * 5);

  resolveInit();
}

function fetchUsers(users = [], cursor = null) {
  let url = "/users/list";

  if (cursor) {
    url += `?cursor=${cursor}`;
  }

  fetch(url)
    .then((response) => response.json())
    .then((data) => {
      users = users.concat(data.users);

      if (data.cursor) {
        fetchUsers(users, data.cursor);

        return;
      }

      rebuildSearch(users);
    })
    .catch((error) => {
      console.log("Error fetching users", error);
      if (!allUsers) allUsers = [];
      resolveInit();
    });
}

async function doRemoteSearch(str, exclude = []) {
  let url = `/users/search?q=${encodeURIComponent(str)}`;

  if (exclude.length > 0) {
    url += `&excl=${exclude.join(",")}`;
  }

  return await fetch(url)
    .then((response) => response.json())
    .then((data) => data.users)
    .catch((error) => {
      console.log("Error fetching users", error);
      return [];
    });
}

function doLocalSearch(str, exclude = []) {
  const exclusions = new Set(exclude);

  let results;

  if (str.length > 0) {
    results = jsSearch.search(str).filter((user) => {
      return !exclusions.has(user.id);
    });
  } else {
    results = (allUsers || []).slice(0, 100).filter((user) => {
      return !exclusions.has(user.id);
    });
  }

  return results;
}

async function search(str, exclude = []) {
  await waitForInit();

  return useRemoteSearch
    ? doRemoteSearch(str, exclude)
    : doLocalSearch(str, exclude);
}

// This is unfortunately complicated because we hit race conditions
// between the init and user trying to search: https://app.rollbar.com/a/getdx/fix/item/dx-production/11426#detail
// This is due to how we load user search on the window and then consume it in alpine components.
async function waitForInit() {
  // If init is undefined, we've hit a race condition between the init function running and user trying to search.
  // So, wait for it to be defined.
  while (initPromise === undefined) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  // Once init has started, wait for it to complete.
  await initPromise;
}

async function findByEmail(email) {
  await waitForInit();

  if (useRemoteSearch) {
    // For remote search, do a direct search by email
    return doRemoteSearch(email, []).then((users) => {
      return users.find((u) => u.email === email) || null;
    });
  } else {
    // For local search, find directly in allUsers array
    return (allUsers || []).find((u) => u.email === email) || null;
  }
}

// Expose functions globally
window.userSearch = {
  search,
  init,
  findByEmail,
};
